// Fichier: src/pages/PaymentForm.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Person as PersonIcon,
  Assignment as PlanIcon,
  Euro as EuroIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const PaymentForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Données du formulaire
  const [formData, setFormData] = useState({
    // Étape 1: Sélection de l'élève
    studentId: '',
    studentInfo: null,

    // Étape 2: Sélection de la formule
    plan: '',
    planType: 'formula', // 'formula' ou 'custom'
    customPlanName: '',
    courseIds: [], // plusieurs cours possibles (carte10, abonnement)
    abonnementFrequency: 'monthly', // monthly | semester1 | semester2 | annual (formule "abonnement" uniquement)
    registrationType: 'child',
    insuranceIncluded: false,

    // Étape 3: Détails du paiement
    amount: '',
    customAmount: '',
    paymentDate: moment().format('YYYY-MM-DD'),
    paymentMethod: 'bank',
    bankReference: '',
    customCommunication: '',
    notes: ''
  });

  const steps = ['Sélection de l\'élève', 'Choix de la formule', 'Détails du paiement'];

  // Prix des formules "à l'unité" — identiques au site public (Registration.js) et à l'app mobile
  const subscriptionPrices = {
    essai: 10,
    unique: 12,
    carte10: 95
  };

  // Grille tarifaire de l'abonnement, par palier (déterminé par le nombre de cours sélectionnés).
  // Reste synchronisé avec tierPricing dans Registration.js (site public) et _tierPricing dans
  // mes_inscriptions.dart (app mobile) — à mettre à jour aux 3 endroits si les tarifs changent.
  const tierPricing = {
    1: { monthly: 40, annual: 320, semester1: 144, semester2: 216 }, // Bronze
    2: { monthly: 70, annual: 560, semester1: 252, semester2: 378 }, // Argent
    3: { monthly: 90, annual: 720, semester1: 324, semester2: 486 }  // Gold (3 cours/semaine ou +)
  };
  const tierName = { 1: 'Bronze', 2: 'Argent', 3: 'Gold' };
  const frequencyLabel = { monthly: 'Mensuel', semester1: 'Semestre 1', semester2: 'Semestre 2', annual: 'Annuel' };

  const isSinglePlan = formData.plan === 'essai' || formData.plan === 'unique';
  const isAbonnementPlan = formData.plan === 'abonnement';
  const abonnementTier = formData.courseIds.length <= 1 ? 1 : (formData.courseIds.length === 2 ? 2 : 3);

  // Charger les données initiales
  useEffect(() => {
    fetchStudents();
    fetchPlans();
    fetchCourses();
  }, []);

  // Filtrer les étudiants selon la recherche
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(student =>
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.firstName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [searchTerm, students]);

  // Recalcule automatiquement le montant total dès que la formule, le type de plan,
  // les cours sélectionnés, la fréquence d'abonnement ou l'assurance changent.
  useEffect(() => {
    let baseAmount = 0;
    if (formData.planType === 'custom') {
      baseAmount = parseFloat(formData.customAmount) || 0;
    } else if (formData.plan === 'abonnement') {
      const tier = formData.courseIds.length <= 1 ? 1 : (formData.courseIds.length === 2 ? 2 : 3);
      baseAmount = tierPricing[tier][formData.abonnementFrequency];
    } else if (formData.plan) {
      baseAmount = subscriptionPrices[formData.plan] || 0;
    }

    const insuranceAmount = formData.insuranceIncluded ? 25 : 0;
    const totalAmount = baseAmount + insuranceAmount;

    setFormData(prev => (
      prev.amount === totalAmount.toString()
        ? prev
        : { ...prev, amount: totalAmount.toString() }
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plan, formData.planType, formData.customAmount, formData.courseIds, formData.abonnementFrequency, formData.insuranceIncluded]);

  const fetchStudents = async () => {
    try {
      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const studentsData = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentsData);
      setFilteredStudents(studentsData);
    } catch (error) {
      console.error('Erreur chargement étudiants:', error);
    }
  };

  const fetchPlans = async () => {
    // Plans prédéfinis, alignés sur le site public (Registration.js)
    const predefinedPlans = [
      {
        id: 'essai',
        name: 'Cours d\'essai',
        price: subscriptionPrices.essai,
        description: 'Découverte, 1 cours'
      },
      {
        id: 'unique',
        name: 'Cours unique',
        price: subscriptionPrices.unique,
        description: 'Un cours ponctuel'
      },
      {
        id: 'carte10',
        name: 'Carte 10 cours',
        price: subscriptionPrices.carte10,
        description: '10 cours valables 6 mois'
      },
      {
        id: 'abonnement',
        name: 'Abonnement',
        price: null, // dépend du nombre de cours choisis et de la fréquence
        description: 'Bronze/Argent/Gold selon le nombre de cours par semaine'
      }
    ];
    setPlans(predefinedPlans);
  };

  const fetchCourses = async () => {
    try {
      const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
      const coursesData = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Extraire le titre principal comme dans Courses.js
        title: doc.data().title || 'Cours sans titre'
      }));
      setCourses(coursesData);
    } catch (error) {
      console.error('Erreur chargement cours:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleStudentSelect = (studentId) => {
    const selectedStudent = students.find(s => s.id === studentId);
    setFormData({
      ...formData,
      studentId,
      studentInfo: selectedStudent
    });
  };

  const handlePlanSelect = (planId) => {
    setFormData(prev => ({
      ...prev,
      plan: planId,
      planType: 'formula',
      customAmount: '',
      customPlanName: '',
      // essai/unique: un seul cours ; on ne garde que le premier si plusieurs étaient sélectionnés
      courseIds: (planId === 'essai' || planId === 'unique') ? prev.courseIds.slice(0, 1) : prev.courseIds
    }));
  };

  const handlePlanTypeChange = (event) => {
    const planType = event.target.value;
    setFormData(prev => ({
      ...prev,
      planType,
      plan: planType === 'custom' ? '' : prev.plan
    }));
  };

  const handleCustomAmountChange = (value) => {
    setFormData(prev => ({
      ...prev,
      customAmount: value
    }));
  };

  const handleInsuranceChange = (e) => {
    setFormData(prev => ({
      ...prev,
      insuranceIncluded: e.target.checked
    }));
  };

  const handleFrequencyChange = (frequency) => {
    setFormData(prev => ({
      ...prev,
      abonnementFrequency: frequency
    }));
  };

  const toggleCourseSelection = (courseId) => {
    setFormData(prev => {
      const single = prev.plan === 'essai' || prev.plan === 'unique';
      if (single) {
        return { ...prev, courseIds: [courseId] };
      }
      const alreadySelected = prev.courseIds.includes(courseId);
      return {
        ...prev,
        courseIds: alreadySelected
          ? prev.courseIds.filter(id => id !== courseId)
          : [...prev.courseIds, courseId]
      };
    });
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Créer une référence unique
      const reference = `MAN-${moment().format('YYYYMMDD-HHmmss')}`;

      // Calculer les montants finaux
      let baseAmount = 0;
      let planName = '';

      if (formData.planType === 'formula') {
        if (formData.plan === 'abonnement') {
          baseAmount = tierPricing[abonnementTier][formData.abonnementFrequency];
          planName = `Abonnement ${tierName[abonnementTier]} (${frequencyLabel[formData.abonnementFrequency]})`;
        } else {
          baseAmount = subscriptionPrices[formData.plan] || 0;
          planName = plans.find(p => p.id === formData.plan)?.name || '';
        }
      } else {
        baseAmount = parseFloat(formData.customAmount) || 0;
        planName = formData.customPlanName || 'Montant personnalisé';
      }

      const insuranceAmount = formData.insuranceIncluded ? 25 : 0;
      const totalAmount = baseAmount + insuranceAmount;

      // Récupérer les cours sélectionnés (0, 1 ou plusieurs)
      const selectedCourseObjs = courses.filter(c => formData.courseIds.includes(c.id));
      const selectedCourses = selectedCourseObjs.map(c => ({ id: c.id, title: c.title }));
      const courseTitle = selectedCourseObjs[0]?.title || 'Cours non spécifié';

      // Préparer les données pour Firestore
      const paymentData = {
        // Informations étudiant
        studentId: formData.studentId,
        studentInfo: formData.studentInfo,

        // Informations formule et cours
        plan: formData.plan,
        planType: formData.planType,
        planName: planName,
        customPlanName: formData.customPlanName,
        // Compatibilité: premier cours sélectionné, exposé aussi en champ simple
        courseId: selectedCourseObjs[0]?.id || '',
        courseTitle: courseTitle,
        // Champs multi-cours (mêmes noms que le site public et l'app mobile)
        selectedCourseIds: formData.courseIds,
        selectedCourses: selectedCourses,
        ...(formData.plan === 'abonnement' ? {
          abonnementTier,
          abonnementTierName: tierName[abonnementTier],
          paymentFrequency: formData.abonnementFrequency
        } : {}),
        registrationType: formData.registrationType,
        insuranceIncluded: formData.insuranceIncluded,

        // Détails paiement
        paymentDetails: {
          baseAmount: baseAmount,
          insuranceAmount: insuranceAmount,
          totalAmount: totalAmount,
          paymentMethod: formData.paymentMethod,
          bankReference: formData.bankReference,
          customCommunication: formData.customCommunication,
          notes: formData.notes
        },

        // Métadonnées - IMPORTANT: statut "pending" pour nouveau paiement
        paymentStatus: 'pending',
        paidAt: formData.paymentMethod !== 'bank' ? new Date(formData.paymentDate) : null,
        createdAt: new Date(),
        reference: reference,

        // Informations de contact (depuis l'étudiant)
        contactInfo: {
          email: formData.studentInfo?.email || '',
          phone: formData.studentInfo?.phone || ''
        },

        // Pour la compatibilité avec l'existant
        persons: [{
          firstName: formData.studentInfo?.firstName || '',
          lastName: formData.studentInfo?.lastName || '',
          studentId: formData.studentId
        }]
      };

      // Ajouter à Firestore
      await addDoc(collection(db, 'registrations'), paymentData);

      // Redirection vers la liste des paiements
      navigate('/payments', {
        state: {
          message: 'Paiement créé avec succès',
          severity: 'success'
        }
      });

    } catch (error) {
      console.error('Erreur création paiement:', error);
      alert('Erreur lors de la création du paiement');
    } finally {
      setLoading(false);
    }
  };

  // Étape 1: Sélection de l'élève
  const renderStudentStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sélectionnez un élève
      </Typography>

      {/* Barre de recherche */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Rechercher un élève par nom ou prénom..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
        }}
        sx={{ mb: 3 }}
      />

      {filteredStudents.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {searchTerm ? 'Aucun élève trouvé pour cette recherche' : 'Aucun élève trouvé. Veuillez d\'abord créer des élèves dans la section dédiée.'}
        </Alert>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredStudents.map((student) => (
            <ListItem
              key={student.id}
              button
              selected={formData.studentId === student.id}
              onClick={() => handleStudentSelect(student.id)}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: formData.studentId === student.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: formData.studentId === student.id ? '#e3f2fd' : 'white',
                '&:hover': {
                  backgroundColor: formData.studentId === student.id ? '#e3f2fd' : '#f5f5f5'
                }
              }}
            >
              <ListItemIcon>
                <PersonIcon color={formData.studentId === student.id ? "primary" : "action"} />
              </ListItemIcon>
              <ListItemText
                primary={`${student.firstName} ${student.lastName}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  // Étape 2: Sélection de la formule
  const renderPlanStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choisissez une formule ou un montant personnalisé
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Type d'inscription</InputLabel>
        <Select
          value={formData.registrationType}
          label="Type d'inscription"
          onChange={(e) => handleInputChange('registrationType', e.target.value)}
        >
          <MenuItem value="child">Enfant</MenuItem>
          <MenuItem value="adult">Adulte</MenuItem>
        </Select>
      </FormControl>

      {/* Sélection du type de plan */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Type de paiement</FormLabel>
        <RadioGroup
          row
          value={formData.planType}
          onChange={handlePlanTypeChange}
        >
          <FormControlLabel value="formula" control={<Radio />} label="Formule prédéfinie" />
          <FormControlLabel value="custom" control={<Radio />} label="Montant personnalisé" />
        </RadioGroup>
      </FormControl>

      {formData.planType === 'formula' ? (
        <Grid container spacing={2}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.id}>
              <Card
                variant={formData.plan === plan.id ? "elevation" : "outlined"}
                elevation={formData.plan === plan.id ? 4 : 0}
                sx={{
                  cursor: 'pointer',
                  border: formData.plan === plan.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  transition: 'all 0.2s',
                  height: '100%'
                }}
                onClick={() => handlePlanSelect(plan.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <PlanIcon color={formData.plan === plan.id ? "primary" : "action"} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plan.description}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      color={plan.price === 0 ? "success.main" : "primary"}
                      fontWeight="bold"
                    >
                      {plan.price === null
                        ? `Dès ${tierPricing[1].monthly}€/mois`
                        : (plan.price === 0 ? 'Gratuit' : `${plan.price}€`)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom de la prestation"
              value={formData.customPlanName}
              onChange={(e) => handleInputChange('customPlanName', e.target.value)}
              placeholder="Ex: Stage été, Frais d'inscription..."
              helperText="Décrivez la raison du paiement"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Montant personnalisé (€)"
              type="number"
              value={formData.customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              placeholder="0.00"
              InputProps={{
                startAdornment: <EuroIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
        </Grid>
      )}

      {/* Assurance */}
      {formData.planType === 'formula' && formData.plan && formData.plan !== 'essai' && formData.plan !== 'unique' && (
        <Card sx={{ mt: 3, p: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.insuranceIncluded}
                onChange={handleInsuranceChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Ajouter l'assurance annuelle (+25€)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Protection et couverture assurance pour l'année
                </Typography>
              </Box>
            }
          />
        </Card>
      )}

      {/* Sélection du/des cours */}
      {formData.plan && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {isSinglePlan ? 'Cours' : 'Cours (sélection multiple possible)'}
          </Typography>
          {!isSinglePlan && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Vous pouvez sélectionner plusieurs cours.
              {isAbonnementPlan && ' Le nombre de cours choisis détermine le palier (1 = Bronze, 2 = Argent, 3+ = Gold).'}
            </Typography>
          )}
          {courses.length === 0 ? (
            <Alert severity="warning">Aucun cours disponible</Alert>
          ) : (
            <Grid container spacing={1}>
              {courses.map((course) => {
                const isSelected = formData.courseIds.includes(course.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={course.id}>
                    <Card
                      variant="outlined"
                      onClick={() => toggleCourseSelection(course.id)}
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        backgroundColor: isSelected ? '#e3f2fd' : 'white',
                        height: '100%'
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {isSelected
                          ? <CheckIcon color="primary" fontSize="small" />
                          : <Box sx={{ width: 20 }} />}
                        <Typography variant="body2">{course.title}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {isAbonnementPlan && formData.courseIds.length > 0 && (
            <Chip
              sx={{ mt: 2 }}
              color="primary"
              variant="outlined"
              label={`Palier ${tierName[abonnementTier]} — ${formData.courseIds.length} cours/semaine`}
            />
          )}
        </Box>
      )}

      {/* Fréquence de paiement de l'abonnement */}
      {isAbonnementPlan && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Fréquence de paiement
          </Typography>
          <Grid container spacing={2}>
            {['monthly', 'semester1', 'semester2', 'annual'].map((freq) => (
              <Grid item xs={6} md={3} key={freq}>
                <Card
                  variant="outlined"
                  onClick={() => handleFrequencyChange(freq)}
                  sx={{
                    cursor: 'pointer',
                    border: formData.abonnementFrequency === freq ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    backgroundColor: formData.abonnementFrequency === freq ? '#e3f2fd' : 'white',
                    textAlign: 'center',
                    p: 1
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {frequencyLabel[freq]}
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {tierPricing[abonnementTier][freq]}€
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Récapitulatif du montant */}
      {(formData.plan || formData.planType === 'custom') && (
        <Card sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa' }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Récapitulatif du montant
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>
              {formData.planType === 'formula'
                ? (formData.plan === 'abonnement'
                    ? `Abonnement ${tierName[abonnementTier]} (${frequencyLabel[formData.abonnementFrequency]}):`
                    : `Formule ${plans.find(p => p.id === formData.plan)?.name}:`)
                : `${formData.customPlanName || 'Montant personnalisé'}:`
              }
            </Typography>
            <Typography>
              {formData.planType === 'formula'
                ? (formData.plan === 'abonnement'
                    ? `${tierPricing[abonnementTier][formData.abonnementFrequency]}€`
                    : `${subscriptionPrices[formData.plan] || 0}€`)
                : `${formData.customAmount || 0}€`
              }
            </Typography>
          </Box>
          {formData.insuranceIncluded && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Assurance:</Typography>
              <Typography>25€</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Total:</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formData.amount}€
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );

  // Étape 3: Détails du paiement
  const renderPaymentStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Détails du paiement
      </Typography>

      {/* Récapitulatif complet */}
      <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Récapitulatif complet
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Élève:</strong></Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {formData.studentInfo?.firstName} {formData.studentInfo?.lastName}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2"><strong>Type:</strong></Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {formData.planType === 'formula' ? 'Formule prédéfinie' : 'Montant personnalisé'}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2"><strong>Prestation:</strong></Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {formData.planType === 'formula'
                  ? (formData.plan === 'abonnement'
                      ? `Abonnement ${tierName[abonnementTier]} (${frequencyLabel[formData.abonnementFrequency]})`
                      : plans.find(p => p.id === formData.plan)?.name)
                  : (formData.customPlanName || 'Montant personnalisé')
                }
              </Typography>
            </Grid>

            {formData.courseIds.length > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>{formData.courseIds.length > 1 ? 'Cours:' : 'Cours:'}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {courses.filter(c => formData.courseIds.includes(c.id)).map(c => c.title).join(', ')}
                  </Typography>
                </Grid>
              </>
            )}

            {formData.insuranceIncluded && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Assurance:</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Incluse (+25€)</Typography>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <Typography variant="body2"><strong>Montant total:</strong></Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {formData.amount}€
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2"><strong>Statut:</strong></Typography>
            </Grid>
            <Grid item xs={6}>
              <Chip
                label="En attente"
                color="warning"
                size="small"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date du paiement"
            type="date"
            value={formData.paymentDate}
            onChange={(e) => handleInputChange('paymentDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Méthode de paiement</InputLabel>
            <Select
              value={formData.paymentMethod}
              label="Méthode de paiement"
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
            >
              <MenuItem value="bank">Virement bancaire</MenuItem>
              <MenuItem value="cash">Espèces</MenuItem>
              <MenuItem value="check">Chèque</MenuItem>
              <MenuItem value="card">Carte bancaire</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Référence bancaire"
            value={formData.bankReference}
            onChange={(e) => handleInputChange('bankReference', e.target.value)}
            placeholder="Ex: VIR123456, CHQ789012"
            helperText="Référence du virement, numéro de chèque, etc."
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Communication personnalisée"
            value={formData.customCommunication}
            onChange={(e) => handleInputChange('customCommunication', e.target.value)}
            placeholder="Ex: Stage été 2024, Frais administratifs..."
            helperText="Communication spécifique pour ce paiement"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes (optionnel)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Informations supplémentaires sur ce paiement..."
          />
        </Grid>
      </Grid>
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderStudentStep();
      case 1:
        return renderPlanStep();
      case 2:
        return renderPaymentStep();
      default:
        return 'Étape inconnue';
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return !!formData.studentId;
      case 1:
        if (formData.planType === 'formula') {
          if (!formData.plan) return false;
          if (formData.plan === 'abonnement' && formData.courseIds.length === 0) return false;
          return true;
        } else {
          return !!formData.customAmount && parseFloat(formData.customAmount) > 0;
        }
      case 2:
        return !!formData.amount && parseFloat(formData.amount) > 0;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Nouveau paiement
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={activeStep === 0 ? () => navigate('/payments') : handleBack}
            startIcon={<ArrowBack />}
          >
            {activeStep === 0 ? 'Retour aux paiements' : 'Précédent'}
          </Button>

          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            disabled={!isStepValid() || loading}
            endIcon={activeStep === steps.length - 1 ? null : <ArrowForward />}
          >
            {activeStep === steps.length - 1
              ? (loading ? 'Création...' : 'Créer le paiement')
              : 'Suivant'
            }
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentForm;